// src/components/IconSelector.js - Updated with URL prefix auto-fill
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link2, Crown, AlertCircle } from 'lucide-react';
import availableIcons from './icons';
import styles from './IconSelector.module.css';
import { saveUserIcons } from './firebaseOperations';
// Import the ProFeatureContext
import { useProFeature } from '../context/ProFeatureContext';
// Import social URL patterns
import socialUrlPatterns from './socialUrlPatterns';

const FREE_ICON_LIMIT = 5;
const PRO_ICON_LIMIT = 25;

// Format URL function (similar to the one in LinksManager.js)
const formatUrl = (url) => {
  // Trim any whitespace
  let formattedUrl = url.trim();
  
  // If URL is empty, return as is
  if (!formattedUrl) return formattedUrl;

  // Check if the URL already has a protocol (http://, https://, ftp://, etc.)
  const hasProtocol = /^[a-zA-Z]+:\/\//.test(formattedUrl) || 
                     formattedUrl.startsWith('mailto:') || 
                     formattedUrl.startsWith('tel:');
  
  // If no protocol is present, add https://
  if (!hasProtocol) {
    formattedUrl = `https://${formattedUrl}`;
  }
  
  return formattedUrl;
};

const IconSelector = ({ userId, onSave, initialIcons = [], currentColor = 'currentColor', isProUser, onUpgradeClick }) => {
  // Add this line to access the ProFeatureContext
  const { showProFeatureNotification } = useProFeature();

  // Merge saved icons with availableIcons.
  // The saved icons store the lookup key (icon name) in the `component` field.
  // We use that to find the actual component from availableIcons while preserving saved properties (like color).
  const [selectedIcons, setSelectedIcons] = useState(
    initialIcons
      .map(icon => {
        const found = availableIcons.find(i => i.name === icon.component);
        return found ? { ...found, ...icon, component: found.component } : null;
      })
      .filter(icon => icon)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);
  const [showUrlWarning, setShowUrlWarning] = useState(false);
  const [navigatingAway, setNavigatingAway] = useState(false);
  const timeoutRef = useRef(null);
  const [inputPlaceholders, setInputPlaceholders] = useState({});

  const categories = useMemo(() => {
    const catSet = new Set();
    availableIcons.forEach(icon => {
      if (icon.category) catSet.add(icon.category);
    });
    return { 
      all: 'All Icons', 
      ...Object.fromEntries(
        Array.from(catSet).map(cat => [cat, cat.charAt(0).toUpperCase() + cat.slice(1)])
      )
    };
  }, []);

  const filteredIcons = useMemo(() => {
    return availableIcons.filter(icon => {
      const matchesCategory = categoryFilter === 'all' || icon.category === categoryFilter;
      const matchesSearch = icon.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, searchTerm]);

  const toggleIcon = (iconObj) => {
    if (selectedIcons.some(i => i.name === iconObj.name)) {
      setSelectedIcons(selectedIcons.filter(i => i.name !== iconObj.name));
    } else if (selectedIcons.length < (isProUser ? PRO_ICON_LIMIT : FREE_ICON_LIMIT)) {
      // Check if we have a URL pattern for this icon
      const urlPattern = socialUrlPatterns[iconObj.name];
      let initialUrl = '';
      
      // If we have a pattern, use the prefix as the initial URL
      if (urlPattern) {
        initialUrl = urlPattern.prefix;
        // Set input placeholder for this icon
        setInputPlaceholders(prev => ({
          ...prev,
          [iconObj.name]: urlPattern.placeholder
        }));
      }
      
      setSelectedIcons([
        ...selectedIcons, 
        { 
          component: iconObj.component,
          url: initialUrl,
          name: iconObj.name,
          category: iconObj.category,
          color: currentColor
        }
      ]);
      
      // Show URL warning when a new icon is added
      setShowUrlWarning(true);
      
      // Clear previous timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Hide warning after 5 seconds
      timeoutRef.current = setTimeout(() => {
        setShowUrlWarning(false);
      }, 5000);
    } else if (!isProUser) {
      // Show pro feature notification when trying to add more than the FREE_ICON_LIMIT
      // UPDATED: Only show notification without auto-redirecting
      showProFeatureNotification(`Upgrade to Pro to add up to ${PRO_ICON_LIMIT} icons`);
    }
    setHasChanges(true);
  };

  const handleColorClick = () => {
    if (!isProUser) {
      // Show pro feature notification when trying to use color customization
      // UPDATED: Only show notification without auto-redirecting
      showProFeatureNotification('Upgrade to Pro to customize icon colors');
    }
  };

  const updateIconUrl = (iconObj, input) => {
    const urlPattern = socialUrlPatterns[iconObj.name];
    let formattedUrl = input;
    
    if (urlPattern) {
      // If we have a URL pattern, always combine prefix + input
      formattedUrl = `${urlPattern.prefix}${input}`;
    } else {
      // For URLs without pattern, ensure proper formatting
      formattedUrl = formatUrl(input);
    }
    
    setSelectedIcons(selectedIcons.map(icon => 
      icon.name === iconObj.name ? { ...icon, url: formattedUrl } : icon
    ));
    setHasChanges(true);
  };

  const updateIconColor = (iconObj, color) => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirecting
      showProFeatureNotification('Upgrade to Pro to customize icon colors');
      return;
    }
    setSelectedIcons(selectedIcons.map(icon => 
      icon.name === iconObj.name ? { ...icon, color } : icon
    ));
    setHasChanges(true);
  };

  const validateUrl = (url) => {
    if (!url) return true;
    
    // Special validation for patterns with prefixes
    for (const iconName in socialUrlPatterns) {
      const pattern = socialUrlPatterns[iconName];
      if (url.startsWith(pattern.prefix)) {
        return url.length > pattern.prefix.length;
      }
    }
    
    // For general URLs
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Check for missing URLs when user tries to leave
  const checkForMissingUrls = useCallback(() => {
    const iconsWithoutUrls = selectedIcons.filter(icon => {
      const urlPattern = socialUrlPatterns[icon.name];
      if (urlPattern) {
        // For pattern URLs, consider them empty if they're just the prefix
        return !icon.url || icon.url === urlPattern.prefix;
      }
      return !icon.url.trim();
    });
    
    if (iconsWithoutUrls.length > 0) {
      setShowUrlWarning(true);
      setNavigatingAway(true);
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Keep warning visible for longer when user is navigating away
      timeoutRef.current = setTimeout(() => {
        setShowUrlWarning(false);
        setNavigatingAway(false);
      }, 10000);
      
      return true;
    }
    return false;
  }, [selectedIcons]);

  // Get placeholder text for URL input
  const getPlaceholder = (iconObj) => {
    const pattern = socialUrlPatterns[iconObj.name];
    if (pattern) {
      return pattern.placeholder;
    }
    return "Enter URL";
  };
  
  // Check if URL has just the prefix
  const hasOnlyPrefix = (iconObj, url) => {
    const pattern = socialUrlPatterns[iconObj.name];
    return pattern && url === pattern.prefix;
  };

  // Save only icons with a valid URL. We store the icon's name as the lookup key in `component`.
  const handleSave = useCallback(async () => {
    try {
      const iconsToSave = selectedIcons
        .filter(icon => {
          // Check if the URL is not empty and not just the prefix
          const pattern = socialUrlPatterns[icon.name];
          if (pattern) {
            return icon.url && icon.url !== pattern.prefix;
          }
          return icon.url.trim();
        })
        .map(icon => ({
          component: icon.name, // save the icon's name as the lookup key
          url: icon.url,
          name: icon.name,
          color: icon.color
        }));

      await saveUserIcons(userId, iconsToSave);
      if (onSave) onSave(iconsToSave);
      setHasChanges(false);
      
      // Warn about missing URLs
      if (selectedIcons.length > iconsToSave.length) {
        setShowUrlWarning(true);
        
        // Clear previous timeout if it exists
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Hide warning after 5 seconds
        timeoutRef.current = setTimeout(() => {
          setShowUrlWarning(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error saving icons:', error);
    }
  }, [selectedIcons, userId, onSave]);

  useEffect(() => {
    if (hasChanges) {
      const timer = setTimeout(() => {
        handleSave();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasChanges, handleSave]);

  // Add blur event listener to the window to catch when user leaves the component
  useEffect(() => {
    const handleBlur = () => {
      checkForMissingUrls();
    };
    
    window.addEventListener('blur', handleBlur);
    
    return () => {
      window.removeEventListener('blur', handleBlur);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [checkForMissingUrls]);

  return (
    <motion.div 
      className={styles.iconSelectorContainer}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onAnimationComplete={() => {
        // Check for empty URLs on initial load
        const missingUrls = selectedIcons.some(icon => {
          const pattern = socialUrlPatterns[icon.name];
          if (pattern) {
            return !icon.url || icon.url === pattern.prefix;
          }
          return !icon.url.trim();
        });
        
        if (missingUrls) {
          setShowUrlWarning(true);
          
          // Hide warning after 5 seconds
          timeoutRef.current = setTimeout(() => {
            setShowUrlWarning(false);
          }, 5000);
        }
      }}
    >
      <div className={styles.selectedIconsPreview}>
        <motion.h3 layout>
          Selected Social Links ({selectedIcons.length}/{isProUser ? PRO_ICON_LIMIT : FREE_ICON_LIMIT})
          <span className={styles.note}>- Enter your username to complete the links</span>
        </motion.h3>
        <motion.div layout className={styles.previewGrid}>
          <AnimatePresence>
            {selectedIcons.map((icon, index) => {
              const urlPattern = socialUrlPatterns[icon.name];
              const hasJustPrefix = urlPattern && icon.url === urlPattern.prefix;
              
              return (
                <motion.div
                  key={index}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className={styles.selectedIconContainer}
                >
                  <div className={styles.iconAndColor}>
                    <div className={styles.selectedIcon}>
                      {React.createElement(icon.component, { 
                        size: 24,
                        color: icon.color || currentColor,
                        stroke: icon.color || currentColor,
                        style: { transition: 'color 0.3s ease' }
                      })}
                      <motion.button 
                        className={styles.removeIcon}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => toggleIcon(icon)}
                      >
                        ×
                      </motion.button>
                    </div>
                    <div 
                      className={`${styles.colorPickerContainer} ${!isProUser ? styles.proFeature : ''}`}
                      onClick={!isProUser ? handleColorClick : undefined}
                    >
                      <input
                        type="color"
                        value={icon.color || currentColor}
                        onChange={(e) => updateIconColor(icon, e.target.value)}
                        className={styles.colorPicker}
                        disabled={!isProUser}
                      />
                      {!isProUser && (
                        <div className={styles.proBadge}>
                          <Crown size={14} className={styles.menuCrown} />
                          <span>PRO</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <motion.div 
                    className={styles.urlInput}
                    initial={false}
                    animate={{ 
                      x: !validateUrl(icon.url) && icon.url ? [-2, 2, -2, 0] : 0 
                    }}
                    transition={{ duration: 0.2 }}
                  >
                  
<input
  type="text"
  placeholder={getPlaceholder(icon)}
  value={urlPattern && icon.url.startsWith(urlPattern.prefix) 
    ? icon.url.substring(urlPattern.prefix.length) // Show only username part
    : icon.url}
  onChange={(e) => {
    // If we have a pattern, update only the username part
    if (urlPattern) {
      const username = e.target.value;
      updateIconUrl(icon, username);
    } else {
      updateIconUrl(icon, e.target.value);
    }
  }}
  className={`${styles.input} ${!validateUrl(icon.url) && icon.url ? styles.invalid : ''} 
    ${!icon.url || hasJustPrefix ? styles.emptyUrl : ''} ${urlPattern ? styles.hasPrefix : ''}`}
/>
{urlPattern && (
  <span className={styles.urlPrefix}>{urlPattern.prefix}</span>
)}
                    <Link2 
                      size={16} 
                      className={`${styles.urlIcon} ${validateUrl(icon.url) && icon.url && !hasJustPrefix ? styles.valid : ''}`}
                    />
                  </motion.div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
        
        {/* URL Warning Message */}
        {showUrlWarning && selectedIcons.some(icon => {
          const pattern = socialUrlPatterns[icon.name];
          if (pattern) {
            return !icon.url || icon.url === pattern.prefix;
          }
          return !icon.url.trim();
        }) && (
          <motion.div 
            className={styles.urlWarning}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <AlertCircle size={16} />
            <span>
              {navigatingAway 
                ? "Hold on! Some icons are missing usernames and won't be saved or function. Please complete your social links before leaving."
                : "Remember to enter your username for each social icon for the links to work correctly."}
            </span>
          </motion.div>
        )}
      </div>

      <motion.div layout className={styles.iconCounter}>
        {selectedIcons.length} of {isProUser ? PRO_ICON_LIMIT : FREE_ICON_LIMIT} icons selected
        {!isProUser && selectedIcons.length >= FREE_ICON_LIMIT && (
          <div className={styles.counterProBadge}>
            <Crown size={14} className={styles.menuCrown} />
            <span>Upgrade to PRO for more icons</span>
          </div>
        )}
      </motion.div>

      <motion.div layout className={styles.controls}>
        <motion.input
          whileFocus={{ scale: 1.02 }}
          type="text"
          placeholder="Search icons..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        <select 
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className={styles.categorySelect}
        >
          {Object.entries(categories).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </motion.div>

      <motion.div 
        layout
        className={styles.iconsGrid}
      >
        <AnimatePresence>
          {filteredIcons.map((iconObj) => {
            const isSelected = selectedIcons.some(i => i.name === iconObj.name);
            const selectedIcon = selectedIcons.find(i => i.name === iconObj.name);
            const atFreeLimit = !isProUser && selectedIcons.length >= FREE_ICON_LIMIT;
            
            return (
              <motion.div
                key={iconObj.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                whileHover={!atFreeLimit || isSelected ? { scale: 1.1 } : {}}
                whileTap={{ scale: 0.95 }}
                className={`${styles.iconItem} ${isSelected ? styles.selected : ''} 
                          ${atFreeLimit && !isSelected ? styles.disabled : ''}`}
                onClick={() => {
                  if (atFreeLimit && !isSelected) {
                    showProFeatureNotification(`Upgrade to Pro to add up to ${PRO_ICON_LIMIT} icons`);
                  } else {
                    toggleIcon(iconObj);
                  }
                }}
              >
                {React.createElement(iconObj.component, { 
                  size: 24,
                  // Always use black for unselected icons, use selected color for selected ones
                  color: isSelected ? (selectedIcon?.color || currentColor) : "#000000",
                  stroke: isSelected ? (selectedIcon?.color || currentColor) : "#000000",
                  style: { transition: 'color 0.3s ease' }
                })}
                <span className={styles.iconName}>{iconObj.name}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default IconSelector;